import { useEffect, useState } from 'react';
import { AIEdit } from './features/ai-edit';

/** Validates that at least one AI API credential set is configured at startup. */
function getApiConfigError(): string | null {
  const hasAzure = !!(import.meta.env.VITE_AZURE_API_KEY && import.meta.env.VITE_AZURE_API_BASE);
  const hasOpenAI = !!import.meta.env.VITE_OPENAI_API_KEY;
  const hasModel = !!import.meta.env.VITE_AI_MODEL;

  if (!hasModel) {
    return 'VITE_AI_MODEL is not configured. Set it to your model deployment name (e.g. gpt-4o).';
  }
  if (!hasAzure && !hasOpenAI) {
    return 'No AI API credentials found. Set VITE_AZURE_API_KEY + VITE_AZURE_API_BASE (for Azure OpenAI) or VITE_OPENAI_API_KEY (for OpenAI).';
  }
  return null;
}

function App() {
  const [isOfficeInitialized, setIsOfficeInitialized] = useState(false);

  const apiConfigError = getApiConfigError();

  // All hooks must be called unconditionally — guards come after
  useEffect(() => {
    if (apiConfigError) return;

    Office.onReady(() => {
      setIsOfficeInitialized(true);

      if (Office.context && Office.context.document) {
        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          updateDocumentText
        );
        updateDocumentText();
      } else {
        console.error('Office.context.document is not available');
      }
    });
  }, [apiConfigError]);

  if (apiConfigError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-6 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Configuration Error</h2>
          <p className="text-sm text-red-700">{apiConfigError}</p>
          <p className="text-xs text-red-500 mt-3">Check your <code>.env</code> file or CI/CD secrets and restart the dev server.</p>
        </div>
      </div>
    );
  }

  const updateDocumentText = async () => {
    try {
      if (typeof Word === 'undefined') {
        console.error('Word object is not available');
        return;
      }

      await Word.run(async (context) => {
        const body = context.document.body;
        context.load(body, 'text');
        await context.sync();
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (!isOfficeInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-600">Initializing...</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white">
      <div className="w-full h-full">
        <AIEdit />
      </div>
    </div>
  );
}

export default App;
